/**
 * CompanyRegisterPage — registers a new shipping company.
 * Mirrors Flutter `company/register_company_page.dart`. The flow:
 *   1. Pick `company_type` (local | international)
 *   2. Pick `operator_mode` (shipper | operator | both)
 *   3. Fill identity + legal numbers + contact
 *   4. Submit → status `draft` → user uploads KYC docs → submits for review
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Building2, Truck } from 'lucide-react';
import { useCreateCompany } from '@/hooks/useCompany';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/common/Button';
import { TextField } from '@/components/common/TextField';
import type { CompanyType, CompanyOperatorMode } from '@/types/company';

export default function CompanyRegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const create = useCreateCompany();

  const [companyType, setCompanyType] = useState<CompanyType>('local');
  const [operatorMode, setOperatorMode] = useState<CompanyOperatorMode>('shipper');
  const [name, setName] = useState('');
  const [nameAr, setNameAr] = useState('');
  const [commercialRegister, setCommercialRegister] = useState('');
  const [taxId, setTaxId] = useState('');
  const [importLicense, setImportLicense] = useState('');
  const [country, setCountry] = useState('سوريا');
  const [city, setCity] = useState('');
  const [address, setAddress] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim() || !contactPhone.trim()) {
      setError(t('err_company_required', 'الاسم ورقم التواصل مطلوبان')); return;
    }
    try {
      await create.mutateAsync({
        companyType,
        operatorMode,
        name: name.trim(),
        nameAr: nameAr.trim() || undefined,
        commercialRegister: commercialRegister.trim() || undefined,
        taxId: taxId.trim() || undefined,
        importLicense: importLicense.trim() || undefined,
        country: country.trim() || undefined,
        city: city.trim() || undefined,
        address: address.trim() || undefined,
        contactPhone: contactPhone.trim(),
        contactEmail: contactEmail.trim() || undefined,
      });
      navigate('/company', { replace: true });
    } catch (e: any) {
      setError(e?.message || t('create_failed', 'فشل التسجيل'));
    }
  }

  return (
    <AppLayout>
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-text-secondary mb-4 hover:text-primary">
        <ArrowRight size={18} /> {t('back', 'رجوع')}
      </button>

      <div className="bg-surface border border-border rounded-2xl p-6 max-w-2xl mx-auto">
        <h1 className="text-xl font-bold text-primary mb-1 flex items-center gap-2">
          <Building2 size={20} /> {t('register_company', 'تسجيل شركة شحن')}
        </h1>
        <p className="text-text-secondary text-sm mb-5">
          {t('register_company_intro', 'بعد إنشاء المسوّدة، ارفع وثائق KYC ثم اضغط "تقديم للمراجعة". الإدارة تراجع وتمنح الصلاحيات تلقائياً.')}
        </p>

        {error && <div className="bg-error/10 text-error rounded-xl p-3 text-sm mb-4">{error}</div>}

        <form onSubmit={submit} className="space-y-5">
          <fieldset className="space-y-3">
            <legend className="text-sm font-semibold text-text-secondary mb-2">
              {t('company_type', 'نوع الشركة')}
            </legend>
            <div className="grid grid-cols-2 gap-2">
              <Card
                active={companyType === 'local'}
                onClick={() => setCompanyType('local')}
                icon={<Truck size={20} />}
                title={t('company_local', 'محلية')}
                subtitle={t('company_local_subtitle', 'نقل داخل سوريا')}
              />
              <Card
                active={companyType === 'international'}
                onClick={() => setCompanyType('international')}
                icon={<Building2 size={20} />}
                title={t('company_international', 'دولية')}
                subtitle={t('company_international_subtitle', 'استيراد/حاويات')}
              />
            </div>
          </fieldset>

          <fieldset className="space-y-2">
            <legend className="text-sm font-semibold text-text-secondary mb-2">
              {t('operator_mode', 'وضع التشغيل')}
            </legend>
            <div className="flex flex-wrap gap-2">
              {(['shipper', 'operator', 'both'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setOperatorMode(m)}
                  className={`px-4 py-2 rounded-xl border text-sm transition ${
                    operatorMode === m
                      ? 'bg-primary text-white border-primary'
                      : 'bg-surface text-text-secondary border-border hover:border-primary'
                  }`}
                >
                  {t(`operator_mode.${m}`, m === 'shipper' ? 'شاحن' : m === 'operator' ? 'مشغّل' : 'كلاهما')}
                </button>
              ))}
            </div>
            <p className="text-xs text-text-tertiary mt-1">
              {operatorMode === 'shipper' && t('mode_shipper_hint', 'تستخدم الشركة لإرسال شحنات باسمها (لا صلاحيات إضافية)')}
              {operatorMode === 'operator' && t('mode_operator_hint',
                companyType === 'international'
                  ? 'تشغّل حاويات دولية (يُمنح container_organizer بعد الموافقة)'
                  : 'تشغّل سائقين/رحلات (يُمنح local_freight_operator بعد الموافقة)')}
              {operatorMode === 'both' && t('mode_both_hint', 'تستخدم وتشغّل في آن واحد')}
            </p>
          </fieldset>

          <fieldset className="space-y-3">
            <legend className="text-sm font-semibold text-text-secondary mb-1">{t('identity', 'الهوية')}</legend>
            <TextField label={t('company_name_en', 'اسم الشركة بالإنكليزية')} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
            <TextField label={t('company_name_ar', 'الاسم بالعربية (اختياري)')} value={nameAr} onChange={(e) => setNameAr(e.target.value)} />
          </fieldset>

          <fieldset className="space-y-3">
            <legend className="text-sm font-semibold text-text-secondary mb-1">{t('legal_numbers', 'الأرقام النظامية')}</legend>
            <TextField label={t('commercial_register', 'السجل التجاري')} dir="ltr" value={commercialRegister} onChange={(e) => setCommercialRegister(e.target.value)} />
            <TextField label={t('tax_id', 'الرقم الضريبي')} dir="ltr" value={taxId} onChange={(e) => setTaxId(e.target.value)} />
            {companyType === 'international' && (
              <TextField label={t('import_license', 'رخصة الاستيراد')} dir="ltr" value={importLicense} onChange={(e) => setImportLicense(e.target.value)} />
            )}
          </fieldset>

          <fieldset className="space-y-3">
            <legend className="text-sm font-semibold text-text-secondary mb-1">{t('contact', 'التواصل')}</legend>
            <TextField label={t('contact_phone', 'هاتف التواصل')} type="tel" dir="ltr" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
            <TextField label={t('contact_email', 'البريد الإلكتروني')} type="email" dir="ltr" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
            <div className="grid grid-cols-2 gap-3">
              <TextField label={t('country', 'البلد')} value={country} onChange={(e) => setCountry(e.target.value)} />
              <TextField label={t('city', 'المدينة')} value={city} onChange={(e) => setCity(e.target.value)} />
            </div>
            <TextField label={t('address', 'العنوان')} value={address} onChange={(e) => setAddress(e.target.value)} />
          </fieldset>

          <Button type="submit" loading={create.isPending} fullWidth>
            {t('create_draft', 'إنشاء مسوّدة')}
          </Button>
        </form>
      </div>
    </AppLayout>
  );
}

function Card({ active, onClick, icon, title, subtitle }: {
  active: boolean; onClick: () => void; icon: React.ReactNode; title: string; subtitle: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-start rounded-2xl p-4 border-2 transition ${
        active ? 'border-primary bg-primary/5' : 'border-border bg-surface hover:border-primary/50'
      }`}
    >
      <div className={active ? 'text-primary mb-2' : 'text-text-secondary mb-2'}>{icon}</div>
      <div className="font-bold text-text-primary mb-0.5">{title}</div>
      <div className="text-xs text-text-tertiary">{subtitle}</div>
    </button>
  );
}
